import { useEffect, useId, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { Controller, FormProvider, useForm } from "react-hook-form";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Button, Input, Select } from "@legal-500/legal500-design-system";
import { fetchAllEditionBpas, researchCms } from "@/api/researchCms.service";
import { usePermissions } from "@/hooks/usePermissions";
import { TaxonomyBpaTierSelector } from "@/components/ResearchCms/TaxonomyBpaTierSelector/TaxonomyBpaTierSelector";
import { StatusBadge } from "@/components/ResearchCms/StatusBadge/StatusBadge";
import { AsyncSearchableSelect } from "@/components/Form/AsyncSearchableSelect/AsyncSearchableSelect";
import {
  CHOOSE_TARGET_REASON,
  mergeHubUrl,
  mergeTargetChoices,
  newTargetNameProblem,
  SPLIT_OPEN_QUERY_KEY,
  startMergeState,
  toMergeRow,
  type MergeRow,
  type MergeTargetChoice,
} from "../../utils/bpaMerge";
import {
  BpaAssignmentFields,
  BpaPickerField,
  RankingEntityPickerField,
  useBpaFormSearches,
} from "../BpaFormFields";
import {
  NewRankingTablesFields,
  type NewRankingTableValues,
} from "../BpaFormFields/NewRankingTablesFields";
import styles from "./CmsBpaMergeDialog.module.css";
import shared from "../BpaImport/importShared.module.css";

const CREATE_VALUE = "create";
const CREATE_NEEDS_PERMISSION_REASON = "Creating practice areas needs create permission.";
const NAMES_NOT_LOADED_REASON =
  "The practice areas in this edition haven't loaded yet, so the new name can't be checked.";
const CREATE_LABEL = "Create practice area";
const NEW_TARGET_LABEL = "New practice area";
const CANCEL_CREATE_LABEL = "Cancel new practice area";
const PICKER_PLACEHOLDER = "Choose a practice area in this edition…";

type CmsBpaMergeDialogProps = {
  editionId: string;
  previousEditionId: string | null;
  editionNamesByEditionId: Map<string, string>;
  ticked: MergeRow[];
  onClose: (createdTarget: boolean) => void;
};

type CreateTargetValues = {
  name: string;
  rankingEntityId: string;
  practiceGroupId: string | null;
  taxonomyBpaId: string | null;
  previousEditionBpaId: string | null;
  assignedResearcherId: string | null;
  assignedEditorId: string | null;
  rankingTables: NewRankingTableValues[];
};

async function fetchSplitOpenIds(editionId: string): Promise<string[]> {
  const overview = await researchCms.getImportOverview(editionId);
  return overview.editionBpas.filter(row => row.splitOpen).map(row => row.editionBpaId);
}

export function splitOpenQuery(editionId: string) {
  return {
    queryKey: [SPLIT_OPEN_QUERY_KEY, editionId],
    queryFn: () => fetchSplitOpenIds(editionId),
  };
}

function isReloading(query: { isSuccess: boolean; isError: boolean; isFetching: boolean }) {
  return !query.isSuccess && (!query.isError || query.isFetching);
}

function hasFailed(query: { isError: boolean; isFetching: boolean }) {
  return query.isError && !query.isFetching;
}

function errorText(err: unknown): string {
  return err instanceof Error ? err.message : "Something went wrong";
}

function choiceKey(choice: MergeTargetChoice): string {
  return choice.kind === "create" ? CREATE_VALUE : choice.row.id;
}

export function CmsBpaMergeDialog({
  editionId,
  previousEditionId,
  editionNamesByEditionId,
  ticked,
  onClose,
}: CmsBpaMergeDialogProps) {
  const titleId = useId();
  const taxonomyLabelId = useId();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { hasPermission } = usePermissions();
  const canCreate = hasPermission("staff.research.create");

  const [selectedKey, setSelectedKey] = useState<string | null>(null);
  const [createdTarget, setCreatedTarget] = useState<MergeRow | null>(null);
  const [error, setError] = useState<string | null>(null);
  const dialogRef = useRef<HTMLDivElement>(null);
  const [entityName, setEntityName] = useState("");

  const editionBpasQuery = useQuery({
    queryKey: ["research-merge-dialog-edition-bpas", editionId],
    queryFn: () => fetchAllEditionBpas({ editionId, includeArchived: true }),
  });
  const editionRows = (editionBpasQuery.data ?? []).map(toMergeRow);
  const splitQuery = useQuery(splitOpenQuery(editionId));
  const researchersQuery = useQuery({
    queryKey: ["research-researchers"],
    queryFn: () => researchCms.getResearchers(),
  });
  const researchers = (researchersQuery.data?.researchers ?? []).map(item => ({
    value: item.id,
    label: item.name,
  }));

  const form = useForm<CreateTargetValues>({
    defaultValues: {
      name: "",
      rankingEntityId: "",
      practiceGroupId: null,
      taxonomyBpaId: null,
      previousEditionBpaId: null,
      assignedResearcherId: null,
      assignedEditorId: null,
      rankingTables: [],
    },
  });
  const rankingEntityId = form.watch("rankingEntityId");
  const { searchPracticeGroups, searchResearchers } = useBpaFormSearches({
    rankingEntityId,
    editionId,
    previousEditionId,
    researchers,
    editionNamesByEditionId,
  });

  const startMutation = useMutation({
    mutationFn: startMerge,
    onSuccess: target => {
      queryClient.invalidateQueries({ queryKey: ["research-edition-bpas"] });
      navigate(mergeHubUrl(editionId, target.bpaId));
    },
    onError: (err: Error) => setError(err.message),
  });
  const starting = startMutation.isPending;

  useEffect(() => {
    const previouslyFocused = document.activeElement;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    dialogRef.current?.focus();
    return () => {
      document.body.style.overflow = previousOverflow;
      if (previouslyFocused instanceof HTMLElement) previouslyFocused.focus();
    };
  }, []);

  const createdAny = createdTarget !== null;

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape" && !starting) onClose(createdAny);
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose, starting, createdAny]);

  const candidatesChecked = splitQuery.isSuccess && editionBpasQuery.isSuccess;
  const baseChoices = mergeTargetChoices({
    ticked,
    editionRows: candidatesChecked ? editionRows : [],
    splitOpenIds: splitQuery.data ?? [],
  });
  const existingChoices = baseChoices.filter(choice => choice.kind === "existing");
  const choices: MergeTargetChoice[] = createdTarget
    ? [...existingChoices, { kind: "existing", row: createdTarget }]
    : baseChoices;
  const choice = choices.find(item => choiceKey(item) === selectedKey) ?? null;
  const creating = choice?.kind === "create";
  const createOffered = choices.some(item => item.kind === "create");
  const start = startMergeState({ ticked, target: choice });
  const startDisabledReason =
    start.disabledReason ?? (creating && !canCreate ? CREATE_NEEDS_PERMISSION_REASON : null);
  const shownReason = startDisabledReason === CHOOSE_TARGET_REASON ? null : startDisabledReason;
  const checkFailed = hasFailed(editionBpasQuery) || hasFailed(splitQuery);
  const checkLoading = !checkFailed && (isReloading(editionBpasQuery) || isReloading(splitQuery));

  function retryCheck() {
    if (hasFailed(editionBpasQuery)) editionBpasQuery.refetch();
    if (hasFailed(splitQuery)) splitQuery.refetch();
  }

  function createProblem(values: CreateTargetValues): string | null {
    if (!editionBpasQuery.isSuccess) return NAMES_NOT_LOADED_REASON;
    const nameProblem = newTargetNameProblem(
      values.name,
      editionRows.map(row => row.bpaName),
    );
    if (nameProblem) return nameProblem;
    if (!values.rankingEntityId) return "Choose a ranking entity.";
    const incomplete = values.rankingTables.some(table => !table.name.trim() || !table.subtype);
    return incomplete ? "Each ranking table needs a name and a subtype." : null;
  }

  async function createTarget(values: CreateTargetValues): Promise<MergeRow> {
    const result = await researchCms.createResearchBpa({
      name: values.name.trim(),
      rankingEntityId: values.rankingEntityId,
      practiceGroupId: values.practiceGroupId,
      editionId,
      taxonomyBpaId: values.taxonomyBpaId,
      previousEditionBpaId: values.previousEditionBpaId,
      assignedResearcherId: values.assignedResearcherId,
      assignedEditorId: values.assignedEditorId,
      rankingTables: values.rankingTables.map(table => ({
        name: table.name.trim(),
        subtype: table.subtype,
        submissionType: table.submissionType || null,
        refereeType: table.refereeType || null,
      })),
    });
    return toMergeRow({
      id: result.editionBpa.id,
      bpaId: result.researchBpa.id as string,
      bpaName: values.name.trim(),
      status: result.editionBpa.status,
      mergeTargetEditionBpaId: null,
      mergeSourceCount: 0,
    });
  }

  async function startMerge(): Promise<MergeRow> {
    let target = choice;
    let created: MergeRow | null = null;
    if (choice?.kind === "create") {
      const values = form.getValues();
      const problem = createProblem(values);
      if (problem) throw new Error(problem);
      try {
        created = await createTarget(values);
      } catch (err) {
        throw new Error(`Creating ${values.name.trim()} failed: ${errorText(err)}`);
      }
      setCreatedTarget(created);
      setSelectedKey(created.id);
      target = { kind: "existing", row: created };
    }
    if (target?.kind !== "existing") throw new Error("Choose where the content goes.");
    const { sourceEditionBpaIds } = startMergeState({ ticked, target });
    try {
      await researchCms.scheduleMerge(target.row.id, sourceEditionBpaIds);
    } catch (err) {
      throw new Error(
        created
          ? `${created.bpaName} was created. Scheduling the merge failed: ${errorText(err)}`
          : errorText(err),
      );
    }
    return target.row;
  }

  function handleStart() {
    if (!choice || startDisabledReason || starting) return;
    setError(null);
    startMutation.mutate();
  }

  function requestClose() {
    if (!starting) onClose(createdAny);
  }

  return createPortal(
    <div
      className={styles.overlay}
      onClick={event => event.target === event.currentTarget && requestClose()}
    >
      <div
        ref={dialogRef}
        className={styles.dialog}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
      >
        <div className={styles.header}>
          <h2 id={titleId} className={styles.title}>
            Merge {ticked.length} practice areas
          </h2>
          <button
            type="button"
            className={styles.closeButton}
            aria-label="Close"
            disabled={starting}
            onClick={requestClose}
          >
            <svg viewBox="0 0 16 16" width="16" height="16" aria-hidden="true" focusable="false">
              <path
                d="M3 3l10 10M13 3L3 13"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                fill="none"
              />
            </svg>
          </button>
        </div>

        <section aria-label="Practice areas to merge">
          <ul className={styles.list}>
            {ticked.map(row => (
              <li key={row.id} className={styles.row}>
                <span className={styles.rowName}>{row.bpaName}</span>
                {row.status && <StatusBadge status={row.status} />}
              </li>
            ))}
          </ul>
        </section>

        <div className={styles.pickerGroup}>
          <div className={styles.picker} data-empty={selectedKey ? undefined : ""}>
            <Select
              id="merge-into"
              label="Merge into"
              fullWidth
              value={selectedKey ?? ""}
              disabled={starting || checkLoading}
              onChange={event => setSelectedKey(event.target.value || null)}
              options={[
                { value: "", label: PICKER_PLACEHOLDER },
                ...choices.flatMap(item =>
                  item.kind === "existing" ? [{ value: item.row.id, label: item.row.bpaName }] : [],
                ),
                ...(creating ? [{ value: CREATE_VALUE, label: NEW_TARGET_LABEL }] : []),
              ]}
            />
          </div>
          {createOffered && !creating && (
            <button
              type="button"
              className={styles.createLink}
              disabled={starting || checkLoading || !canCreate}
              title={canCreate ? undefined : CREATE_NEEDS_PERMISSION_REASON}
              onClick={() => setSelectedKey(CREATE_VALUE)}
            >
              + {CREATE_LABEL}
            </button>
          )}
          {creating && (
            <button
              type="button"
              className={styles.createLink}
              disabled={starting}
              onClick={() => setSelectedKey(null)}
            >
              {CANCEL_CREATE_LABEL}
            </button>
          )}
        </div>

        <p className={styles.description}>
          The ticked practice areas will show as Scheduled for merge. Each one is then imported, one
          by one, into the chosen target.
        </p>

        {checkFailed && (
          <div className={styles.problem} role="alert">
            <span>
              {hasFailed(editionBpasQuery)
                ? "Could not load the edition's practice areas, so a target can't be chosen or named yet."
                : "Could not load the edition's other practice areas. Only a new practice area can be chosen until they load."}
            </span>
            <Button variant="outlined" color="default" size="small" onClick={retryCheck}>
              Retry
            </Button>
          </div>
        )}

        {choice?.kind === "existing" && (
          <p className={styles.note}>
            {choice.row.bpaName} keeps its name and taxonomy BPA. To merge into a different name,
            choose {CREATE_LABEL} and create a new target.
          </p>
        )}

        {creating && (
          <FormProvider {...form}>
            <div className={styles.createForm} aria-label="New practice area">
              <Input
                id="merge-target-name"
                label="Practice area name"
                fullWidth
                disabled={starting}
                {...form.register("name")}
              />
              <div className={styles.field} role="group" aria-labelledby={taxonomyLabelId}>
                <span id={taxonomyLabelId} className={styles.fieldLabel}>
                  Taxonomy BPA
                </span>
                <Controller
                  name="taxonomyBpaId"
                  control={form.control}
                  render={({ field }) => (
                    <TaxonomyBpaTierSelector
                      value={field.value}
                      onChange={value => field.onChange(value)}
                      disabled={starting}
                    />
                  )}
                />
              </div>
              <div className={styles.field}>
                <Controller
                  name="rankingEntityId"
                  control={form.control}
                  render={({ field }) => (
                    <RankingEntityPickerField
                      value={field.value || null}
                      onChange={value => field.onChange(value ?? "")}
                      onEntityChange={entity =>
                        setEntityName(entity ? `${entity.name} (${entity.type})` : "")
                      }
                      selectedLabel={entityName || null}
                    />
                  )}
                />
              </div>
              <div className={styles.field}>
                <Controller
                  name="practiceGroupId"
                  control={form.control}
                  render={({ field }) => (
                    <AsyncSearchableSelect
                      name="practiceGroupId"
                      label="Practice group"
                      value={field.value}
                      onChange={value => field.onChange(value ?? null)}
                      onSearch={searchPracticeGroups}
                      placeholder={rankingEntityId ? "Search groups..." : "Select entity first"}
                      disabled={!rankingEntityId}
                      minSearchLength={0}
                    />
                  )}
                />
              </div>
              <div className={styles.readOnly}>
                <Input
                  id="merge-target-edition"
                  label="Edition"
                  fullWidth
                  disabled
                  readOnly
                  value={editionNamesByEditionId.get(editionId) ?? ""}
                />
              </div>
              <BpaAssignmentFields
                plain
                control={form.control}
                searchResearchers={searchResearchers}
              />
              <div className={styles.field}>
                <Controller
                  name="previousEditionBpaId"
                  control={form.control}
                  render={({ field }) => (
                    <BpaPickerField
                      label="Previous edition BPA"
                      editionId={previousEditionId}
                      editionNamesByEditionId={editionNamesByEditionId}
                      value={field.value}
                      onChange={value => field.onChange(value)}
                      disabled={!previousEditionId}
                      disabledPlaceholder="No previous edition"
                    />
                  )}
                />
              </div>
              <NewRankingTablesFields />
            </div>
          </FormProvider>
        )}

        {error && (
          <p className={styles.problem} role="alert">
            {error}
          </p>
        )}

        <div className={styles.footer}>
          {checkLoading && (
            <span className={styles.reason} role="status">
              Loading the edition's practice areas...
            </span>
          )}
          {shownReason && (
            <span id="merge-start-reason" className={styles.reason}>
              {shownReason}
            </span>
          )}
          <Button
            className={shared.compactButton}
            variant="outlined"
            color="default"
            disabled={starting}
            onClick={requestClose}
          >
            Cancel
          </Button>
          <Button
            className={shared.compactButton}
            variant="contained"
            color="primary"
            disabled={startDisabledReason !== null || starting}
            title={startDisabledReason ?? undefined}
            aria-describedby={shownReason ? "merge-start-reason" : undefined}
            onClick={handleStart}
          >
            {starting ? "Starting..." : "Start merge"}
          </Button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
